<template>
  <div>
    <section class="hero section">
      <div class="container">
        <p class="eyebrow">1:1 Coaching & Strategic Advisory</p>
        <h1>Personalized work at<br>the moments that matter most.</h1>
        <p class="hero-subtext">
          Kayla works with a deliberately small number of clients at a time.
          This isn't a program with modules. It's a working relationship -
          part coaching, part mentorship, part strategic advisory —
          built around what you actually need.
        </p>
      </div>
    </section>

    <section class="work-is section">
      <div class="container">
        <h2>How it works</h2>
        <p>
          Engagements are structured around the client, not a curriculum.
          That means sessions are part strategy, part performance science,
          part operational thinking; whatever the moment requires.
        </p>
        <p>
          Kayla brings the same rigor to a career conversation that she
          brings to organizational design: clear-eyed diagnosis, evidence-informed
          approach, and a bias toward the moves that actually change outcomes.
        </p>
        <p>Most clients begin working with Kayla over a defined engagement. Many continue into longer-term partnerships or ongoing advisory; with some maintaining an evergreen relationship and reconnecting as needed over time.</p>
        <p>The relationship is designed to be flexible and responsive; a consistent, trusted resource as new challenges and decisions emerge.</p>

        <div class="client-logos">
          <p class="client-logos-intro">Recent clients have come from organizations including:</p>
          <div class="logo-carousel" aria-label="Recent client organizations">
            <div class="logo-track">
              <span class="logo-item">Asana</span>
              <span class="logo-item">Amazon</span>
              <span class="logo-item">Anthropic</span>
              <span class="logo-item">Chainalysis</span>
              <span class="logo-item">Cursor</span>
              <span class="logo-item">Deloitte</span>
              <span class="logo-item">FIU</span>
              <span class="logo-item">GE Healthcare</span>
              <span class="logo-item">General Catalyst</span>
              <span class="logo-item">Grammarly (Superhuman)</span>
              <span class="logo-item">Greylock Partners</span>
              <span class="logo-item">Harvard University</span>
              <span class="logo-item">LinkedIn</span>
              <span class="logo-item">MIT</span>
              <span class="logo-item">Moody’s Analytics</span>
              <span class="logo-item">OneProject</span>
              <span class="logo-item">Roku</span>
              <span class="logo-item">Takeda</span>
              <span class="logo-item">Thermo Fisher Scientific</span>
              <span class="logo-item">The Trade Desk</span>
              <span class="logo-item">Uber</span>
              <span class="logo-item">University of Miami</span>
              <span class="logo-item">United Way</span>
              <span class="logo-item">Vanta</span>
              <span class="logo-item">Asana</span>
              <span class="logo-item">Amazon</span>
              <span class="logo-item">Anthropic</span>
              <span class="logo-item">Chainalysis</span>
              <span class="logo-item">Cursor</span>
              <span class="logo-item">Deloitte</span>
              <span class="logo-item">FIU</span>
              <span class="logo-item">GE Healthcare</span>
              <span class="logo-item">General Catalyst</span>
              <span class="logo-item">Grammarly (Superhuman)</span>
              <span class="logo-item">Greylock Partners</span>
              <span class="logo-item">Harvard University</span>
              <span class="logo-item">LinkedIn</span>
              <span class="logo-item">MIT</span>
              <span class="logo-item">Moody’s Analytics</span>
              <span class="logo-item">OneProject</span>
              <span class="logo-item">Roku</span>
              <span class="logo-item">Takeda</span>
              <span class="logo-item">Thermo Fisher Scientific</span>
              <span class="logo-item">The Trade Desk</span>
              <span class="logo-item">Uber</span>
              <span class="logo-item">University of Miami</span>
              <span class="logo-item">United Way</span>
              <span class="logo-item">Vanta</span>
            </div>
          </div>
          <p class="client-logos-note">Representative, not exhaustive. Includes both organizational and individual engagements.</p>
        </div>
      </div>
    </section>

    <section class="focus-areas section">
      <div class="container">
        <h2>Focus Areas</h2>
        <div class="focus-grid">
          <div class="focus-column">
            <ul>
              <li>Leadership development and executive effectiveness</li>
              <li>Career acceleration and strategic transitions</li>
              <li>Stepping into or expanding leadership roles</li>
              <li>Decision-making under pressure and ambiguity</li>
              <li>Navigating complex organizational politics</li>
            </ul>
          </div>
          <div class="focus-column">
            <ul>
              <li>Performance optimization and sustainable output</li>
          
              <li>Compensation strategy and negotiation</li>
              <li>Building influence and professional networks</li>
              <li>Long-term career architecture and legacy</li>
              <li>Defining and moving toward a purpose-led next chapter</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="science section">
      <div class="container">
        <h2>Why performance science changes the conversation</h2>
        <p>
          Most leadership coaching relies on frameworks that were built for
          a different era of work and a narrower slice of the workforce.
          They're not wrong, exactly — they're just incomplete.
        </p>
        <p>
          Kayla's approach is grounded in the current state of behavioral science
          and neuroscience research: what the evidence actually says about
          how leaders make decisions under cognitive load, how motivation
          degrades and recovers, what separates high-performing teams from
          averages ones at a biological and psychological level, and how
          to create the conditions for consistent performance rather than
          waiting for peak states.
        </p>
        <p>This isn't jargon. It's practical. And it changes what you pay attention to, what you stop worrying about, and what interventions actually move the needle.</p>
      </div>
    </section>

    <section class="high-achieving section" style="background-color: var(--color-surface);">
      <div class="container">
        <div class="high-achieving-grid">
          <div class="high-achieving-image">
            <img src="/images/EditorialShoot.JPG" alt="Kayla Vatalaro editorial shoot" />
          </div>
          <div>
            <h2>If you've been building<br>without the map — this is for you.</h2>
            <p>
              There's a particular kind of exhaustion that comes from being
              exceptional at your work inside an institution that wasn't
              designed for you. You've navigated it. You've figured it out.
              And you've likely done it with less margin for error, fewer
              informal advocates, and a more complicated relationship with
              visibility than your peers.
            </p>
            <p>
              Kayla spent a decade learning to navigate those same rooms.
              She knows what it costs, and she knows what changes when someone
              finally gives you the map.
            </p>
            <p>
              This work is precise, strategic, and focused on results.
            </p>

            <div class="callout">
              <blockquote>
                "For me, this work is personal. I spent years learning to claim
                a seat at the table. Now I help others build their own."
              </blockquote>
              <cite>— Kayla Vatalaro, Interview with Education Magazine</cite>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="cta-section section">
      <div class="container">
        <div class="cta-content">
          <h2>Let's talk about what you're working on.</h2>
          <p>
            If you're considering 1:1 work, start with a conversation.
            No pressure, no pitch. Just an honest discussion about
            where you are, where you want to be, and whether this is the right fit.
          </p>
          <router-link to="/contact" class="btn">Start a Conversation</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
</script>

<style scoped>
.hero {
  padding-top: 120px;
  padding-bottom: 120px;
}

.hero h1 {
  margin-bottom: 1.5rem;
}

.hero-subtext {
  font-size: var(--text-body-lg);
  color: var(--color-muted);
  margin-bottom: 0;
  max-width: 600px;
}

.work-is h2,
.focus-areas h2,
.science h2 {
  margin-bottom: 2rem;
}

.work-is p,
.science p {
  margin-bottom: 1.5rem;
}

.client-logos {
  margin-top: 2rem;
  text-align: center;
}

.client-logos-intro {
  margin: 0 auto 1rem;
  font-family: 'DM Serif Display', serif;
  font-size: var(--text-body);
  line-height: 1.3;
  font-weight: 600;
  text-align: center;
  max-width: 720px;
}

.logo-carousel {
  overflow: hidden;
  background: transparent;
  border-radius: 16px;
  padding: 1rem 0;
}

.logo-track {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: max-content;
  animation: scroll-logos 40s linear infinite;
}

.logo-item {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  padding: 0.75rem 1rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.03);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-fg);
  white-space: nowrap;
}

@keyframes scroll-logos {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

.client-logos-note {
  margin-top: 0.75rem;
  font-size: var(--text-sm);
  color: var(--color-muted);
  text-align: center;
}

.focus-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  margin-top: 2rem;
}

.focus-column ul {
  list-style: none;
  padding: 0;
}

.focus-column li {
  margin-bottom: 0.5rem;
  padding-left: 1rem;
  position: relative;
}

.focus-column li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--color-accent);
}

.high-achieving {
  background-color: var(--color-surface);
}

.high-achieving-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.45fr) minmax(0, 0.55fr);
  gap: 48px;
  align-items: start;
}

.high-achieving-image img {
  width: 100%;
  border-radius: 24px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.08);
  display: block;
}

.high-achieving h2 {
  margin-bottom: 2rem;
}

.high-achieving p {
  margin-bottom: 1.5rem;
}

.cta-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.cta-content h2 {
  margin-bottom: 1rem;
}

.cta-content p {
  font-size: var(--text-body-lg);
  color: var(--color-muted);
  margin-bottom: 2rem;
  text-align: center;
}

@media (max-width: 640px) {
  .hero {
    padding-top: 80px;
    padding-bottom: 80px;
  }

  .focus-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .high-achieving-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}
</style>


